import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFamily } from '../hooks/useFamilies';
import { useIndividuals } from '../hooks/useIndividuals';
import { useAddFamilyMember, useRemoveFamilyMember, useDeleteFamily, useUpdateFamily } from '../hooks/useFamilies';
import { useDebouncedValue } from '../hooks/useDebounce';
import { Individual } from '../services/individualsService';
import { formatDate } from '../utils/dateUtils';
import '../styles/FamilyDetailPage.css';

const FamilyDetailPage = () => {
  const { familyID } = useParams<{ familyID: string }>();
  const navigate = useNavigate();
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const [relationshipType, setRelationshipType] = useState('Child');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPrimaryID, setEditPrimaryID] = useState('');
  const [editSearch, setEditSearch] = useState('');
  const debouncedEditSearch = useDebouncedValue(editSearch, 300);

  // Lazy load family - only fetch when familyID is available
  const { data: family, isLoading: loadingFamily, error: familyError } = useFamily(
    familyID ? parseInt(familyID) : undefined
  );

  // Lazy load individuals - only fetch when "Add Member" modal is open.
  // Search the SERVER (searchQuery), not just the first 100 rows: the backend
  // caps pageSize at 100, so with a large caseload a client past the first page
  // (by last name) would otherwise be unreachable when adding a family member.
  const {
    data: individualsData,
    isLoading: loadingIndividuals,
    isFetching: fetchingIndividuals,
  } = useIndividuals({
    isActive: true,
    page: 1,
    pageSize: 100,
    searchQuery: debouncedSearch || undefined,
    enabled: showAddMember // Only fetch when modal is open (true lazy loading)
  });

  // Individuals for the edit picker (server-side search across ALL active clients)
  const {
    data: editIndividualsData,
    isLoading: editLoading,
    isFetching: editFetching,
  } = useIndividuals({
    isActive: true,
    page: 1,
    pageSize: 100,
    searchQuery: debouncedEditSearch || undefined,
    enabled: showEdit,
  });
  const editIndividuals: Individual[] = editIndividualsData?.data || [];

  // Gate each picker's "not found" empty-state while its own search is still
  // in flight (loading, background-fetching, or the debounce hasn't caught
  // up yet) so we never flash "not found" for a query that hasn't resolved.
  const isSearchingAddMember =
    loadingIndividuals || fetchingIndividuals || debouncedSearch !== searchTerm;
  const isSearchingEdit =
    editLoading || editFetching || debouncedEditSearch !== editSearch;

  // Mutations with optimistic updates
  const addMemberMutation = useAddFamilyMember();
  const removeMemberMutation = useRemoveFamilyMember();
  const deleteFamilyMutation = useDeleteFamily();
  const updateFamilyMutation = useUpdateFamily();

  const individuals: Individual[] = individualsData?.data || [];
  const loading = loadingFamily;
  const errorMessage = familyError ? (familyError instanceof Error ? familyError.message : 'Failed to load family') : null;

  // Only filter individuals if modal is open (lazy loading)
  const availableIndividuals = showAddMember
    ? individuals.filter(ind =>
        !family?.members?.some(member => member.individualID === ind.individualID)
      )
    : [];

  const filteredAvailableIndividuals = availableIndividuals.filter(ind => {
    const firstName = (ind.firstName || '').toLowerCase();
    const lastName = (ind.lastName || '').toLowerCase();
    const clientID = (ind.clientID || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return `${firstName} ${lastName}`.includes(term) || clientID.includes(term);
  });

  const handleAddMember = async () => {
    if (!selectedIndividual || !familyID) {return;}

    setActionError(null);
    try {
      await addMemberMutation.mutateAsync({
        familyID: parseInt(familyID),
        member: {
          individualID: parseInt(selectedIndividual),
          relationshipType
        }
      });
      setShowAddMember(false);
      setSelectedIndividual('');
      setRelationshipType('Child');
      setSearchTerm('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Unable to add member. Please try again.');
    }
  };

  const handleRemoveMember = async (memberID: number) => {
    if (!confirm('Are you sure you want to remove this member from the family?') || !familyID) {
      return;
    }

    setActionError(null);
    try {
      await removeMemberMutation.mutateAsync({
        familyID: parseInt(familyID),
        memberID
      });
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Unable to remove member. Please try again.');
    }
  };

  const handleDeleteFamily = async () => {
    if (!confirm('Are you sure you want to delete this family? This action cannot be undone.') || !familyID) {
      return;
    }

    setActionError(null);
    try {
      await deleteFamilyMutation.mutateAsync(parseInt(familyID));
      navigate('/families');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Unable to delete family. Please try again.');
    }
  };

  const openEdit = () => {
    setActionError(null);
    setEditName(family?.familyName || '');
    setEditPrimaryID(family?.primaryContactID ? String(family.primaryContactID) : '');
    setEditSearch('');
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!familyID || !editName.trim() || !editPrimaryID) { return; }
    setActionError(null);
    try {
      await updateFamilyMutation.mutateAsync({
        familyID: parseInt(familyID),
        data: { familyName: editName.trim(), primaryContactID: parseInt(editPrimaryID) },
      });
      setShowEdit(false);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Unable to update family. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="family-detail-page">
        <div className="loading">Loading family details...</div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="family-detail-page">
        <div className="error">Family not found</div>
      </div>
    );
  }

  return (
    <div className="family-detail-page">
      <div className="page-header">
        <div>
          <button className="back-button" onClick={() => navigate('/families')}>
            ← Back to Families
          </button>
          <h1>{family.familyName}</h1>
          <p className="subtitle">{family.members?.length || 0} member{family.members?.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => { setActionError(null); setShowAddMember(true); }}>
            + Add Member
          </button>
          <button className="btn-secondary" onClick={openEdit}>
            Edit Family
          </button>
          <button className="btn-danger" onClick={handleDeleteFamily}>
            Delete Family
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          {errorMessage}
          <button onClick={() => window.location.reload()}>×</button>
        </div>
      )}

      {actionError && (
        <div className="alert alert-error">{actionError}</div>
      )}

      <div className="family-info">
        <h2>Family Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="label">Family Name:</span>
            <span className="value">{family.familyName}</span>
          </div>
          <div className="info-item">
            <span className="label">Primary Contact:</span>
            <span className="value">{family.primaryContactName} ({family.primaryContactClientID})</span>
          </div>
          <div className="info-item">
            <span className="label">Created:</span>
            <span className="value">
              {formatDate(family.createdAt, 'N/A')}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Last Updated:</span>
            <span className="value">
              {formatDate(family.updatedAt, 'N/A')}
            </span>
          </div>
        </div>
      </div>

      <div className="family-members">
        <h2>Family Members</h2>

        {family.members && family.members.length > 0 ? (
          <div className="members-table">
            <table>
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Name</th>
                  <th>Date of Birth</th>
                  <th>Gender</th>
                  <th>Relationship</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {family.members.map(member => (
                  <tr key={member.familyMemberID} className={member.isPrimaryContact ? 'primary-contact' : ''}>
                    <td>{member.clientID}</td>
                    <td>
                      {member.firstName} {member.lastName}
                      {member.isPrimaryContact && <span className="badge">Primary</span>}
                    </td>
                    <td>
                      {formatDate(member.dateOfBirth, 'N/A')}
                    </td>
                    <td>{member.gender || 'N/A'}</td>
                    <td>{member.relationshipType}</td>
                    <td>
                      {member.phoneNumber && <div>{member.phoneNumber}</div>}
                      {member.emailAddress && <div className="email">{member.emailAddress}</div>}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-action btn-log-visit"
                        onClick={() => navigate(`/clients/log-visit?clientId=${member.clientID}`)}
                        title="Log a service visit for this member"
                      >
                        Log Visit
                      </button>
                      {!member.isPrimaryContact && (
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveMember(member.familyMemberID!)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>No members in this family yet</p>
            <button className="btn-primary" onClick={() => { setActionError(null); setShowAddMember(true); }}>
              Add First Member
            </button>
          </div>
        )}
      </div>

      {showAddMember && (
        <div className="modal-overlay" onClick={() => setShowAddMember(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Family Member</h2>
              <button className="close-button" onClick={() => setShowAddMember(false)}>×</button>
            </div>

            <div className="modal-body">
              {actionError && <div className="alert alert-error">{actionError}</div>}

              <div className="form-group">
                <label>Search for Individual</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or Client ID..."
                  className="search-input"
                />
              </div>

              <div className="individuals-list">
                {isSearchingAddMember ? (
                  <div className="empty-state">
                    <p>Searching…</p>
                  </div>
                ) : filteredAvailableIndividuals.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? (
                      <p>No individuals found matching &quot;{searchTerm}&quot;</p>
                    ) : (
                      <p>No available individuals. All individuals are already members of this family.</p>
                    )}
                  </div>
                ) : (
                  filteredAvailableIndividuals.map(individual => (
                      <label key={individual.individualID} className="individual-option">
                        <input
                          type="radio"
                          name="individual"
                          value={individual.individualID}
                          checked={selectedIndividual === individual.individualID?.toString()}
                          onChange={(e) => setSelectedIndividual(e.target.value)}
                        />
                        <div className="individual-details">
                          <div className="individual-name">
                            {individual.firstName} {individual.lastName}
                          </div>
                          <div className="individual-id">{individual.clientID}</div>
                        </div>
                      </label>
                    ))
                )}
              </div>

              <div className="form-group">
                <label>Relationship to Family</label>
                <select
                  value={relationshipType}
                  onChange={(e) => setRelationshipType(e.target.value)}
                >
                  <option value="Child">Child</option>
                  <option value="Parent">Parent</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Grandparent">Grandparent</option>
                  <option value="Grandchild">Grandchild</option>
                  <option value="Other Relative">Other Relative</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowAddMember(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleAddMember}
                disabled={!selectedIndividual}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Family</h2>
              <button className="close-button" onClick={() => setShowEdit(false)}>×</button>
            </div>
            <div className="modal-body">
              {actionError && <div className="alert alert-error">{actionError}</div>}
              <div className="form-group">
                <label htmlFor="editFamilyName">Family Name</label>
                <input id="editFamilyName" type="text" value={editName}
                       onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="editPrimarySearch">Primary Contact</label>
                <input id="editPrimarySearch" type="text" className="search-input"
                       placeholder="Search by name or Client ID..."
                       value={editSearch} onChange={(e) => setEditSearch(e.target.value)} />
              </div>
              <div className="individuals-list">
                {isSearchingEdit ? (
                  <div className="empty-state">
                    <p>Searching…</p>
                  </div>
                ) : editIndividuals.length === 0 ? (
                  <div className="empty-state">
                    {editSearch ? (
                      <p>No individuals found matching &quot;{editSearch}&quot;</p>
                    ) : (
                      <p>No individuals available.</p>
                    )}
                  </div>
                ) : (
                  editIndividuals.map((individual) => (
                    <label key={individual.individualID} className="individual-option">
                      <input type="radio" name="editPrimary" value={individual.individualID}
                             checked={editPrimaryID === individual.individualID?.toString()}
                             onChange={(e) => setEditPrimaryID(e.target.value)} />
                      <div className="individual-details">
                        <div className="individual-name">{individual.firstName} {individual.lastName}</div>
                        <div className="individual-id">{individual.clientID}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveEdit}
                      disabled={!editName.trim() || !editPrimaryID || updateFamilyMutation.isPending}>
                {updateFamilyMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyDetailPage;
